import Vue from 'vue'
import {
  RESET_STATE,
  SET_TEMP_DISARM,
  REMOVE_TEMP_DISARM,
  REMOVE_TEMP_DISARMS,
  SET_ARMING_REQUEST,
  REMOVE_ARMING_REQUEST,
  REMOVE_ARMING_REQUESTS,
  SET_TEMP_DISARM_NOW,
  SET_TEMP_DISARM_SEEDED,
  SET_TEMP_DISARM_TICKER,
  SET_TEMP_DISARM_CREATE,
  SET_SECURITY_ACTION_ERROR,
} from '@/store/mutation-types'
import {
  mapTempDisarm,
  mapTempDisarmApprovalRequest,
  mapArmingApprovalRequest,
  mapArmingStatusTempDisarm,
  tempDisarmDurationSeconds,
} from '@/models/tempDisarm'
import { parseServerTime } from '@/models/bypass'
import { describeActionError } from '@/models/securityActionError'
import { markApprovalResolved, isApprovalResolved } from '@/models/resolvedApprovals'
import apiTempDisarm from '@/apis/siteTempDisarm'
import apiApproval from '@/apis/siteApproval'
import apiPartition from '@/apis/sitePartition'

// ─────────────────────────────────────────────────────────────────────────────
// Temporary Disarm store (MOCK) — mirrors the Camera Bypass module but PER
// PARTITION. A record means "this partition is temporarily disarmed until endTime,
// then auto re-arms". No backend yet; actions mimic async API calls.
//   - map of partitionId -> record
//   - a 1s "now" ticker so countdowns re-render + fixed-duration disarms expire
//   - operator-initiated requests wait for the end-user's approval (awaiting)
// State is PER-SITE (records carry siteId); getters are scoped to selectedSiteId.
// ─────────────────────────────────────────────────────────────────────────────

let tickerHandle = null

// Clock-skew correction (ms): server_time − client Date.now(), captured from the
// pending-list response. Countdowns/expiry run on a server-aligned "now" so a
// wrong client clock can't re-arm early/late. 0 in mock mode. (backend guidance 2026-08-21)
let serverOffset = 0

// Apply a PartitionActionResult body (temp-disarm create/approve) to the store.
// sid = the site the action targeted; stamped onto records so site-scoped
// getters can see them (#1).
function commitTempDisarmResult (commit, body, sid) {
  if (!body) return
  const tempDisarms = body.temp_disarms || []
  tempDisarms.forEach(td => {
    const rec = mapTempDisarm(td, sid)
    if (rec.status === 'active') commit(SET_TEMP_DISARM, { partitionId: rec.partitionId, record: rec })
    else commit(REMOVE_TEMP_DISARM, { partitionId: rec.partitionId })
  })
  if (body.approval_request) {
    const rec = mapTempDisarmApprovalRequest(body.approval_request, sid)
    commit(SET_TEMP_DISARM, { partitionId: rec.partitionId, record: rec })
  }
  const approvalRequests = body.approval_requests || []
  approvalRequests.forEach(ar => {
    const rec = mapTempDisarmApprovalRequest(ar, sid)
    commit(SET_TEMP_DISARM, { partitionId: rec.partitionId, record: rec })
  })
}

const initialState = {
  disarms: {}, // partitionId -> record
  // partitionId -> pending arm/disarm approval request (action: 'arm' | 'disarm').
  // Separate from `disarms` so it never collides with a temp-disarm record.
  armingRequests: {},
  now: Date.now(),
  seededSiteId: null,
  // Global end-user temporary-disarm create dialog (rendered app-wide from App.vue).
  createOpen: false,
  createCtx: null, // { partitionId, siteId, partitionName }
}

const state = Vue.util.extend({}, initialState)

// Surface a failed user-initiated action in the app-wide error dialog, then
// re-throw so callers can still react. Background fetches stay silent.
// SET_SECURITY_ACTION_ERROR lives in the bypass module (modules aren't namespaced).
function failAction (commit, err) {
  commit(SET_SECURITY_ACTION_ERROR, describeActionError(err))
  throw err
}

const getters = {
  tempDisarmRecord: function (state) {
    return function (partitionId) {
      return state.disarms[partitionId] || null
    }
  },
  // A partition is "temporarily disarmed" only once active (not awaiting approval).
  isPartitionTempDisarmed: function (state) {
    return function (partitionId) {
      const r = state.disarms[partitionId]
      return !!r && r.status === 'active'
    }
  },
  // Active temporary disarms on the CURRENTLY SELECTED site.
  tempDisarmRecords: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.disarms).filter(r => r.status === 'active' && r.siteId === sid)
  },
  // Operator-initiated requests awaiting the end-user's approval (selected site).
  tempDisarmRequests: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.disarms).filter(r => r.status === 'awaiting' && r.siteId === sid)
  },
  tempDisarmCount: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.disarms).filter(r => r.status === 'active' && r.siteId === sid).length
  },
  tempDisarmAwaitingCount: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.disarms).filter(r => r.status === 'awaiting' && r.siteId === sid).length
  },
  // Pending arm/disarm approval requests (MC asks to arm/disarm a partition).
  armingRequests: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.armingRequests).filter(r => r.siteId === sid)
  },
  armingAwaitingCount: function (state, getters, rootState, rootGetters) {
    const sid = rootGetters.selectedSiteId
    return Object.values(state.armingRequests).filter(r => r.siteId === sid).length
  },
  tempDisarmNow: function (state) {
    return state.now
  },
  tempDisarmCreateOpen: function (state) {
    return state.createOpen
  },
  tempDisarmCreateCtx: function (state) {
    return state.createCtx
  },
}

const actions = {
  // Start the ticker and load pending temp-disarm requests. Safe to call repeatedly
  // (on mount / site change).
  initTempDisarm: function ({ commit, dispatch }, { siteId } = {}) {
    startTicker(commit)
    return dispatch('fetchTempDisarms', { siteId })
  },

  // Fetch live temp-disarm state for a site (real API). Two sources:
  //  - PENDING requests → approval-requests(action=temp_disarm)
  //  - ACTIVE temp-disarms → partition arming-status (is_temp_disarmed +
  //    temp_disarm_expires_at). This is what survives a page reload; without it
  //    active temp-disarms only lived in create/approve/MQTT responses in-session.
  // The two branches touch disjoint statuses ('awaiting' vs 'active') so they
  // don't interfere. Best-effort re-sync — swallow 4xx/5xx, never reject.
  fetchTempDisarms: function ({ commit, state, rootGetters }, { siteId } = {}) {
    const sid = siteId || rootGetters.selectedSiteId
    startTicker(commit)
    const warn = (where, err) => {
      const msg = 'fetchTempDisarms ' + where + ' failed: ' + (err && err.status)
      Vue.logger ? Vue.logger.warn(msg) : console.warn(msg)
    }
    // One call for all pending requests, routed client-side by action. The core
    // `action` query filter is a single enum (no CSV — only `status` accepts CSV),
    // so we omit it and pick out the partition ones here (temp_disarm → disarms,
    // arm/disarm → armingRequests; bypass/clear_bypass belong to the bypass store).
    const pending = apiApproval.listApprovalRequests({ siteId: sid, status: 'pending' }).then(res => {
      const body = (res && res.body) || {}
      const st = parseServerTime(body.server_time)
      if (st != null) serverOffset = st - Date.now()
      const serverNow = Date.now() + serverOffset
      const requests = body.approval_requests || []
      // Upsert pending records from the response — do NOT wipe-all first. Every
      // relevant MQTT message triggers a fetch, so two requests arriving close
      // together (e.g. an arm + a disarm) fire concurrent fetches; a wipe-then-
      // readd there lets the second fetch's response (which may omit the other,
      // still-in-flight request due to list eventual-consistency) drop valid
      // pending requests — the header chip and approval modal then vanish.
      // Resolved requests fall off via the 2-min timeout ticker (auto-decline)
      // or an explicit approve/decline, so merging instead of wiping is safe.
      requests.forEach(ar => {
        // The backend may still list requests whose 2-min window already elapsed
        // (it hasn't resolved them to expired/rejected yet). Never surface those.
        const exp = parseServerTime(ar.expires_at)
        if (exp != null && exp <= serverNow) return
        // Already resolved locally / elsewhere — the list just hasn't caught up.
        // Re-adding it would pop the approval modal for a finished request.
        if (isApprovalResolved(ar.id, serverNow)) return
        if (ar.action === 'arm' || ar.action === 'disarm') {
          const rec = mapArmingApprovalRequest(ar, sid)
          if (rec.partitionId != null) commit(SET_ARMING_REQUEST, { partitionId: rec.partitionId, record: rec })
        } else if (ar.action === 'temp_disarm') {
          const rec = mapTempDisarmApprovalRequest(ar, sid)
          if (rec.partitionId != null) commit(SET_TEMP_DISARM, { partitionId: rec.partitionId, record: rec })
        }
      })
    }).catch(err => warn('pending', err))
    const active = apiPartition.getArmingStatus({ siteId: sid }).then(res => {
      const partitions = (res && res.body && res.body.partitions) || []
      const activeIds = new Set()
      partitions.forEach(p => {
        const rec = mapArmingStatusTempDisarm(p, sid)
        if (!rec || rec.partitionId == null) return
        activeIds.add(String(rec.partitionId))
        const existing = state.disarms[rec.partitionId]
        // Preserve richer in-session attribution/duration; refresh authoritative endTime.
        if (existing && existing.status === 'active') {
          commit(SET_TEMP_DISARM, { partitionId: rec.partitionId, record: { ...existing, endTime: rec.endTime } })
        } else {
          commit(SET_TEMP_DISARM, { partitionId: rec.partitionId, record: rec })
        }
      })
      // Drop active records for this site whose partition is no longer temp-disarmed.
      const staleActive = Object.keys(state.disarms).filter(id =>
        String(state.disarms[id].siteId) === String(sid) &&
        state.disarms[id].status === 'active' &&
        !activeIds.has(String(id)))
      if (staleActive.length) commit(REMOVE_TEMP_DISARMS, { partitionIds: staleActive })
    }).catch(err => warn('arming-status', err))
    return Promise.all([pending, active]).then(() => commit(SET_TEMP_DISARM_SEEDED, sid))
  },

  // End-user directly temporarily disarms a partition (applied immediately).
  tempDisarmPartition: function ({ commit, rootGetters }, { partitionId, siteId, durationKey }) {
    startTicker(commit)
    const sid = siteId || rootGetters.selectedSiteId
    return apiTempDisarm.createTempDisarm({
      siteId: sid,
      partitions: [{ partitionId, duration: tempDisarmDurationSeconds(durationKey) }],
    }).then(res => commitTempDisarmResult(commit, res && res.body, sid))
      .catch(err => failAction(commit, err))
  },

  // End-user approves a pending (operator-initiated) request.
  approveTempDisarmRequest: function ({ commit, state, rootGetters }, { partitionId }) {
    const r = state.disarms[partitionId]
    if (!r || r.status !== 'awaiting' || !r.approvalRequestId) return Promise.resolve()
    const sid = r.siteId || rootGetters.selectedSiteId
    return apiApproval.approveApprovalRequest({ siteId: sid, requestId: r.approvalRequestId }).then(res => {
      markApprovalResolved(r.approvalRequestId, r.requestExpiresAt)
      // ApprovalResolveResult carries approval_request (no temp_disarm entity),
      // so derive the active window from the approved request.
      const ar = (res && res.body && res.body.approval_request) || null
      if (ar && (ar.status === 'approved' || ar.resolution === 'approved')) {
        const start = parseServerTime(ar.resolved_at) || Date.now()
        const endTime = ar.requested_duration ? start + ar.requested_duration * 1000 : null
        commit(SET_TEMP_DISARM, {
          partitionId,
          record: { ...mapTempDisarmApprovalRequest(ar, sid), status: 'active', startTime: start, endTime, requestExpiresAt: null },
        })
      } else {
        commit(REMOVE_TEMP_DISARM, { partitionId })
      }
    }).catch(err => failAction(commit, err))
  },

  // End-user rejects a pending request.
  rejectTempDisarmRequest: function ({ commit, state, rootGetters }, { partitionId }) {
    const r = state.disarms[partitionId]
    if (!r || !r.approvalRequestId) {
      commit(REMOVE_TEMP_DISARM, { partitionId })
      return Promise.resolve()
    }
    const sid = r.siteId || rootGetters.selectedSiteId
    return apiApproval.declineApprovalRequest({ siteId: sid, requestId: r.approvalRequestId })
      .then(() => {
        markApprovalResolved(r.approvalRequestId, r.requestExpiresAt)
        commit(REMOVE_TEMP_DISARM, { partitionId })
      })
      .catch(err => failAction(commit, err))
  },

  // End-user approves a pending arm/disarm request → the server applies the
  // partition arm/disarm (followThroughApprovedArming). Refresh arming status so
  // the arming UI reflects the new state right away.
  approveArmingRequest: function ({ commit, state, rootGetters, dispatch }, { partitionId }) {
    const r = state.armingRequests[partitionId]
    if (!r || !r.approvalRequestId) return Promise.resolve()
    const sid = r.siteId || rootGetters.selectedSiteId
    return apiApproval.approveApprovalRequest({ siteId: sid, requestId: r.approvalRequestId }).then(() => {
      markApprovalResolved(r.approvalRequestId, r.requestExpiresAt)
      commit(REMOVE_ARMING_REQUEST, { partitionId })
      return dispatch('getPartitionArmingStatus', { siteId: sid }).catch(() => {})
    }).catch(err => failAction(commit, err))
  },

  // End-user rejects a pending arm/disarm request → nothing changes on the partition.
  rejectArmingRequest: function ({ commit, state, rootGetters }, { partitionId }) {
    const r = state.armingRequests[partitionId]
    if (!r || !r.approvalRequestId) {
      commit(REMOVE_ARMING_REQUEST, { partitionId })
      return Promise.resolve()
    }
    const sid = r.siteId || rootGetters.selectedSiteId
    return apiApproval.declineApprovalRequest({ siteId: sid, requestId: r.approvalRequestId })
      .then(() => {
        markApprovalResolved(r.approvalRequestId, r.requestExpiresAt)
        commit(REMOVE_ARMING_REQUEST, { partitionId })
      })
      .catch(err => failAction(commit, err))
  },

  // A monitoring-center-side resolution arrived via MQTT (approval_request.resolved:
  // cancelled by the requester, or approved/declined/expired elsewhere). The pending
  // list is eventually-consistent, so we drop the exact request by partition id here
  // instead of relying on a refetch (which could still list it and re-add it). On an
  // approval the partition state actually changed, so pull the resulting state.
  resolveApprovalRequest: function ({ commit, state, dispatch, rootGetters }, { approvalRequest, siteId }) {
    const ar = approvalRequest || {}
    // Remember it regardless of action, so a lagging pending list can't
    // resurrect this request (and pop the approval modal again).
    markApprovalResolved(ar.id, parseServerTime(ar.expires_at))
    const pid = ar.partition_id
    if (pid == null) return Promise.resolve()
    const sid = siteId || rootGetters.selectedSiteId
    const resolution = ('' + (ar.resolution || ar.status || '')).toLowerCase()
    if (ar.action === 'arm' || ar.action === 'disarm') {
      commit(REMOVE_ARMING_REQUEST, { partitionId: pid })
    } else if (ar.action === 'temp_disarm') {
      // Only drop the pending (awaiting) record; an approved temp-disarm turns
      // active and is refreshed below.
      const rec = state.disarms[pid]
      if (rec && rec.status === 'awaiting') commit(REMOVE_TEMP_DISARM, { partitionId: pid })
    } else {
      return Promise.resolve()
    }
    // Approved → the partition armed/disarmed (or temp-disarmed); refresh the
    // resulting state. Cancel/decline/expire change nothing, so skip the refetch.
    if (resolution === 'approved') {
      if (ar.action === 'temp_disarm') return dispatch('fetchTempDisarms', { siteId: sid }).catch(function () {})
      return dispatch('getPartitionArmingStatus', { siteId: sid }).catch(function () {})
    }
    return Promise.resolve()
  },

  // Re-arm early (clear the temporary disarm now). No app "clear temp-disarm"
  // endpoint exists — re-arming the partition is a normal ARM (arming_action=1).
  clearTempDisarm: function ({ commit, state, rootGetters }, { partitionId }) {
    const r = state.disarms[partitionId]
    const sid = (r && r.siteId) || rootGetters.selectedSiteId
    return apiPartition.updatePartitionArmingStatus({ siteId: sid, partitionIds: [partitionId], armingAction: 1 })
      .then(() => commit(REMOVE_TEMP_DISARM, { partitionId }))
      .catch(err => failAction(commit, err))
  },

  openTempDisarmCreate: function ({ commit }, ctx = {}) {
    commit(SET_TEMP_DISARM_CREATE, { open: true, ctx })
  },
  closeTempDisarmCreate: function ({ commit }) {
    commit(SET_TEMP_DISARM_CREATE, { open: false })
  },
}

const mutations = {
  [RESET_STATE]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  [SET_TEMP_DISARM]: function (state, { partitionId, record }) {
    state.disarms = { ...state.disarms, [partitionId]: record }
  },
  [REMOVE_TEMP_DISARM]: function (state, { partitionId }) {
    const next = { ...state.disarms }
    delete next[partitionId]
    state.disarms = next
  },
  [REMOVE_TEMP_DISARMS]: function (state, { partitionIds }) {
    const next = { ...state.disarms }
    ;(partitionIds || []).forEach(id => { delete next[id] })
    state.disarms = next
  },
  [SET_ARMING_REQUEST]: function (state, { partitionId, record }) {
    state.armingRequests = { ...state.armingRequests, [partitionId]: record }
  },
  [REMOVE_ARMING_REQUEST]: function (state, { partitionId }) {
    const next = { ...state.armingRequests }
    delete next[partitionId]
    state.armingRequests = next
  },
  [REMOVE_ARMING_REQUESTS]: function (state, { partitionIds }) {
    const next = { ...state.armingRequests }
    ;(partitionIds || []).forEach(id => { delete next[id] })
    state.armingRequests = next
  },
  [SET_TEMP_DISARM_NOW]: function (state, now) {
    state.now = now
    let next = null
    for (const id of Object.keys(state.disarms)) {
      const r = state.disarms[id]
      // Active disarm expired → auto re-arm (clear).
      if (r.status === 'active' && r.endTime != null && r.endTime <= now) {
        if (!next) next = { ...state.disarms }
        delete next[id]
        continue
      }
      // Pending request hit the 2-min window with no response → auto-decline.
      if (r.status === 'awaiting' && r.requestExpiresAt != null && r.requestExpiresAt <= now) {
        if (!next) next = { ...state.disarms }
        delete next[id]
      }
    }
    if (next) state.disarms = next
    // Same 2-min timeout drop for pending arm/disarm requests.
    let nextArm = null
    for (const id of Object.keys(state.armingRequests)) {
      const r = state.armingRequests[id]
      if (r.requestExpiresAt != null && r.requestExpiresAt <= now) {
        if (!nextArm) nextArm = { ...state.armingRequests }
        delete nextArm[id]
      }
    }
    if (nextArm) state.armingRequests = nextArm
  },
  [SET_TEMP_DISARM_SEEDED]: function (state, siteId) {
    state.seededSiteId = siteId
  },
  [SET_TEMP_DISARM_TICKER]: function (state, running) {
    state._tickerRunning = running
  },
  [SET_TEMP_DISARM_CREATE]: function (state, { open, ctx }) {
    state.createOpen = open
    if (ctx !== undefined) state.createCtx = ctx
  },
}

// ── helpers ──────────────────────────────────────────────────────────────────

function startTicker (commit) {
  if (tickerHandle) return
  commit(SET_TEMP_DISARM_TICKER, true)
  tickerHandle = setInterval(() => {
    commit(SET_TEMP_DISARM_NOW, Date.now() + serverOffset)
  }, 1000)
}

export default {
  state,
  getters,
  actions,
  mutations,
}
